import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { Finding, FindingOwner } from '../../audit.model';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FindingService } from '../../finding.service';
import { Observable, of, ReplaySubject, timer, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { Employee } from '../../../shared/shared.model';
import { EmployeesService } from '../../../shared/employees.service';
import { MatSelect } from '@angular/material';

@Component({
  selector: 'iaud-finding-edit-form',
  templateUrl: './finding-edit-form.component.html',
  styleUrls: ['./finding-edit-form.component.scss'],
})
export class FindingEditFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('owners') owners: MatSelect;

  today: Date = moment().toDate();
  findingForm: FormGroup;
  owners$: Observable<Array<FindingOwner>>;

  @Input()
  finding: Finding;
  @Input() isNew: boolean = false;
  @Output()
  isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  isFinishLaterValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  maxDate: Date = moment().toDate();

  protected _onDestroy = new Subject<void>();
  searching: boolean = false;
  public filteredOwners: ReplaySubject<Array<Employee>> = new ReplaySubject<Array<Employee>>(1);
  public previousOwners: Array<Employee>;

  constructor(
    private formBuilder: FormBuilder,
    private findingService: FindingService,
    private empSvc: EmployeesService
  ) { }

  ngOnInit(): void {
    this.findingForm = this.generateForm();

    //this.owners$ = this.findingService.getFindingOwners();

    if (this.findingForm.get('owners').value) {
      this.previousOwners = this.findingForm.get('owners').value;
      this.filteredOwners.next(this.findingForm.get('owners').value);
    }

    this.findingForm.statusChanges
      .subscribe(() => {
        this.isValidChange.emit(this.findingForm.valid);
        this.isFinishLaterValidChange
          .emit(this.findingForm.get('title').valid && this.findingForm.get('dueDate').valid);
      });

    this.findingForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(values => values.title)
      )
      .subscribe(values => {
        this.finding.findingTitle = values.title;
        this.finding.findingCode = values.code;
        this.finding.dueDate = values.dueDate;
        this.finding.findingOwner = values.owners;
        this.finding.findingDescription = values.description;
        this.finding.auditDueDate = values.auditDueDate;        
      });

    this.findingForm.get('ownersFilter').valueChanges
      .pipe(
        filter(search => !!search),
        tap(() => this.searching = true),
        takeUntil(this._onDestroy),
        debounceTime(200),
        map(search => {
          return this.empSvc.list(search).pipe(
            map(userList => {
              if (this.previousOwners) {
                this.previousOwners.forEach(x => {
                  if (!userList.find(emp => emp.userId === x.userId)) {
                    userList.push(x);
                  }
                });
              }

              return userList;
            })
          );
        })
      )
      .subscribe(filteredEmps => {
        this.searching = false;
        filteredEmps.subscribe(x => {
          return this.filteredOwners.next(x);
        });
      },
        _error => {
          this.searching = false;
        });
  }

  ngAfterViewInit(): void {
    this.owners.compareWith = (a: Employee, b: Employee) => {
      return a && b && a.userId === b.userId;
    };

    this.owners.valueChange
      .subscribe(values => {
        let restoreSelectedValues = true;

        if (values && values.length && this.previousOwners && Array.isArray(this.previousOwners)) {
          const optionValues = this.owners.options.map(option => option.value);
          this.previousOwners.forEach(previousValue => {
            if (values.indexOf(previousValue) === -1 && optionValues.indexOf(previousValue) === 1) {
              values.push(previousValue);
              restoreSelectedValues = true;
            }
          });
        }
        if (restoreSelectedValues) {
          this.owners._onChange(values);
        }

        this.previousOwners = values;
      });
  }

  generateForm(): FormGroup {
    //let v: Array<string>;
    // if (this.finding.findingOwner && this.finding.findingOwner.length > 0) {
    //   v = this.finding.findingOwner.map(x => x.userId);
    // }

    return this.formBuilder.group({
      title: [this.finding.findingTitle,
              [Validators.required, Validators.maxLength(50)],
              this.isFindingNameAvailableValidator.bind(this)],
      code: [this.finding.findingCode, Validators.maxLength(50)],
      dueDate: [this.finding.dueDate, Validators.required],      
      owners: [this.finding.findingOwner, Validators.required],
      ownersFilter: null,
      description: [this.finding.findingDescription, [Validators.maxLength(2000), Validators.required]],
    });
  }

  isFindingNameAvailableValidator(control: AbstractControl): Observable<ValidationErrors> {
    return timer(500)
      .pipe(
        switchMap(() => {
          if (!this.isNew)
            return of(null);

          return this.findingService.checkFindingExistence(this.finding.auditId, control.value)
            .pipe(
              distinctUntilChanged(),
              map((exists: boolean) => exists ? {exists: true} : null)
            );
        })
      );
  }
  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
